import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCreateSchool } from '@/hooks/use-schools';
import { useCreateCollege } from '@/hooks/use-colleges';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Upload, Building2, MapPin, Settings, Palette, Mail, Loader2, GraduationCap, School } from 'lucide-react';
import { toast } from 'sonner';
import type { SchoolCreate, CollegeCreate, SchoolStatus } from '@/types/api';
import {
  SCHOOL_STATUSES,
  LITE_LEVELS,
  COUNTRIES,
  getStateOptions,
  COLOR_SWATCHES,
  TIMEZONES,
} from '@/config/constants';
import {
  validateSchoolName,
  validateLatitude,
  validateLongitude,
  validateImageFile,
  SCHOOL_NAME_REGEX,
} from '@/lib/validation';

type OrgType = 'school' | 'college';

function getStatusBadgeVariant(status: string): 'default' | 'secondary' | 'destructive' {
  switch (status) {
    case 'live':
      return 'default';
    case 'waitlist':
      return 'secondary';
    case 'suspended':
      return 'destructive';
    default:
      return 'secondary';
  }
}

export function NewSchoolPage() {
  const navigate = useNavigate();
  const createSchool = useCreateSchool();
  const createCollege = useCreateCollege();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const addressSuggestionsRef = useRef<HTMLDivElement>(null);

  const [orgType, setOrgType] = useState<OrgType>('school');
  const isCollege = orgType === 'college';
  const label = isCollege ? 'College' : 'School';

  const [formData, setFormData] = useState<Partial<SchoolCreate>>({
    schoolName: '',
    schoolTitle: '',
    nickname: '',
    website: '',
    ncesCode: '',
    status: 'waitlist',
    liteLevel: 'L1',
    domains: [],
    timezone: 'America/New_York',
    state: '',
    country: 'US',
    lat: undefined,
    lng: undefined,
    studentsCount: 0,
    private: false,
    primaryColor: '',
  });

  const [mascot, setMascot] = useState<File | null>(null);
  const [mascotPreview, setMascotPreview] = useState<string | null>(null);
  const mascotInputRef = useRef<HTMLInputElement>(null);
  const [domainInput, setDomainInput] = useState('');
  const [logo, setLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [addressInput, setAddressInput] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);

  const stateOptions = getStateOptions((formData.country as 'US' | 'CA') || 'US');
  const isPending = createSchool.isPending || createCollege.isPending;

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (addressInput.trim().length < 3) {
        setAddressSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      setIsLoadingSuggestions(true);
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?` +
          `q=${encodeURIComponent(addressInput)}&` +
          `format=json&` +
          `addressdetails=1&` +
          `limit=5`,
          {
            headers: {
              'User-Agent': 'Saturn Admin Dashboard',
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setAddressSuggestions(data);
          setShowSuggestions(data.length > 0);
        }
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      } finally {
        setIsLoadingSuggestions(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      fetchSuggestions();
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [addressInput]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (addressSuggestionsRef.current && !addressSuggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.schoolName || !formData.schoolTitle || !formData.timezone) {
      toast.error('Please fill in all required fields');
      return;
    }

    const schoolNameError = validateSchoolName(formData.schoolName);
    if (schoolNameError) {
      toast.error(schoolNameError);
      return;
    }

    const latError = validateLatitude(formData.lat);
    if (latError) {
      toast.error(latError);
      return;
    }

    const lngError = validateLongitude(formData.lng);
    if (lngError) {
      toast.error(lngError);
      return;
    }

    if (formData.state && formData.country) {
      const validStates = getStateOptions((formData.country as 'US' | 'CA') || 'US');
      if (!validStates.some(s => s.value === formData.state)) {
        toast.error(`Selected state/province is not valid for ${formData.country === 'CA' ? 'Canada' : 'United States'}`);
        return;
      }
    }

    if (isCollege) {
      if (!formData.nickname || !formData.state) {
        toast.error('Nickname and state are required for colleges');
        return;
      }
      if (formData.lat === undefined || formData.lng === undefined) {
        toast.error('Latitude and longitude are required for colleges');
        return;
      }
      if (!formData.website) {
        toast.error('Website is required for colleges');
        return;
      }
      if (!formData.primaryColor) {
        toast.error('Primary color is required for colleges');
        return;
      }

      const collegeData: CollegeCreate = {
        friendlyName: formData.schoolName!.toLowerCase().replace(/[^a-z0-9_]/g, ''),
        schoolTitle: formData.schoolTitle!,
        nickname: formData.nickname!,
        status: formData.status as SchoolStatus,
        timezone: formData.timezone!,
        country: formData.country || 'US',
        state: formData.state!,
        lat: formData.lat!,
        lng: formData.lng!,
        private: formData.private ?? false,
        domains: formData.domains || [],
        studentsCount: formData.studentsCount || 0,
        primaryColor: formData.primaryColor!,
        ipedsId: formData.ncesCode || null,
        website: formData.website!,
        mascotId: null,
      };

      try {
        const result = await createCollege.mutateAsync({
          college: collegeData,
          mascot: mascot || undefined,
        });
        navigate(`/dashboard/schools/${result.friendlyName}`);
      } catch (error) {
        console.error('Failed to create college:', error);
      }
    } else {
      try {
        await createSchool.mutateAsync({
          school: formData as SchoolCreate,
          logo: logo || undefined,
        });
        toast.success('School created successfully!');
        navigate(`/dashboard/schools/${formData.schoolName}`);
      } catch (error) {
        console.error('Failed to create school:', error);
      }
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const error = validateImageFile(file);
      if (error) {
        toast.error(error);
        e.target.value = '';
        return;
      }
      setLogo(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMascotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const error = validateImageFile(file);
      if (error) {
        toast.error(error);
        e.target.value = '';
        return;
      }
      setMascot(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setMascotPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const addDomain = () => {
    if (domainInput && !formData.domains?.includes(domainInput)) {
      setFormData({
        ...formData,
        domains: [...(formData.domains || []), domainInput],
      });
      setDomainInput('');
    }
  };

  const removeDomain = (domain: string) => {
    setFormData({
      ...formData,
      domains: formData.domains?.filter((d) => d !== domain) || [],
    });
  };

  const processGeocodedResult = (result: any) => {
    const address = result.address;

    const countryCode = address.country_code?.toUpperCase();
    let mappedCountry = 'US';
    if (countryCode === 'CA') {
      mappedCountry = 'CA';
    } else if (countryCode === 'US') {
      mappedCountry = 'US';
    }

    const stateRaw = address.state || address.province || address['ISO3166-2-lvl4'] || '';

    let stateCode = '';
    if (stateRaw) {
      const stateOpts = getStateOptions(mappedCountry as 'US' | 'CA');

      let matchedState = stateOpts.find(s => s.value.toUpperCase() === stateRaw.toUpperCase());

      if (!matchedState) {
        matchedState = stateOpts.find(s => s.label.toLowerCase() === stateRaw.toLowerCase());
      }

      if (!matchedState) {
        matchedState = stateOpts.find(s =>
          stateRaw.toLowerCase().includes(s.label.toLowerCase()) ||
          s.label.toLowerCase().includes(stateRaw.toLowerCase())
        );
      }

      if (!matchedState && stateRaw.includes('-')) {
        const parts = stateRaw.split('-');
        const stateAbbr = parts[parts.length - 1];
        matchedState = stateOpts.find(s => s.value.toUpperCase() === stateAbbr.toUpperCase());
      }

      stateCode = matchedState?.value || '';
    }

    setFormData(prev => ({
      ...prev,
      lat: parseFloat(result.lat),
      lng: parseFloat(result.lon),
      country: mappedCountry,
    }));

    setTimeout(() => {
      setFormData(prev => ({
        ...prev,
        state: stateCode,
      }));
    }, 0);

    toast.success('Address populated successfully!');
  };

  const cleanAddressDisplay = (displayName: string, address?: any): string => {
    if (address) {
      const parts: string[] = [];

      const street = address.road || address.street || address.address29 || address.address28;
      if (street) {
        const houseNumber = address.house_number || '';
        parts.push(houseNumber ? `${houseNumber} ${street}` : street);
      }

      const city = address.city || address.town || address.village || address.municipality;
      if (city) parts.push(city);

      const state = address.state || address.province;
      if (state) {
        if (state.length <= 2) {
          parts.push(state);
        } else {
          parts.push(state);
        }
      }

      const postal = address.postcode || address.postal_code;
      if (postal) parts.push(postal);

      const country = address.country;
      if (country) parts.push(country);

      return parts.join(', ');
    }

    const parts = displayName.split(', ');
    const filtered: string[] = [];

    const skipPatterns = [
      /Community Board/i,
      /County$/i,
      /Borough$/i,
      /District$/i,
      /Township$/i,
      /Municipality$/i,
      /Region$/i,
      /Subdivision/i,
      /^Manhattan$/i,
      /^Brooklyn$/i,
      /^Queens$/i,
      /^Bronx$/i,
      /^Staten Island$/i,
    ];

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];

      if (skipPatterns.some(pattern => pattern.test(part))) {
        continue;
      }

      if (filtered.includes(part)) {
        continue;
      }

      if (filtered.length >= 5) break;

      filtered.push(part);
    }

    return filtered.join(', ');
  };

  const handleSelectSuggestion = (suggestion: any) => {
    setAddressInput(cleanAddressDisplay(suggestion.display_name, suggestion.address));
    setShowSuggestions(false);
    processGeocodedResult(suggestion);
  };

  return (
    <div className="container mx-auto space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/dashboard/schools">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight">Create New {label}</h1>
          <p className="text-muted-foreground">
            Add a new {label.toLowerCase()} to the Saturn platform
          </p>
        </div>
      </div>

      {/* Organization Type Toggle */}
      <Tabs value={orgType} onValueChange={(v) => setOrgType(v as OrgType)}>
        <TabsList>
          <TabsTrigger value="school" className="gap-1.5">
            <School className="h-4 w-4" />
            High School
          </TabsTrigger>
          <TabsTrigger value="college" className="gap-1.5">
            <GraduationCap className="h-4 w-4" />
            College
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <form onSubmit={handleSubmit}>
        <div className="grid gap-6">
          {/* Basic Information & Domains */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {isCollege ? <GraduationCap className="h-5 w-5" /> : <Building2 className="h-5 w-5" />}
                Basic Information
              </CardTitle>
              <CardDescription>
                Core {label.toLowerCase()} details, identification, and email domains
              </CardDescription>
            </CardHeader>
            <CardContent>
                  <div className="grid gap-6 md:grid-cols-2">
                    {/* Left Column - Basic Info */}
                    <div className="space-y-5">
              {/* ID */}
              <div className="space-y-2">
                <Label htmlFor="schoolName">
                  {label} ID <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="schoolName"
                  placeholder={isCollege ? 'e.g., mit_ma' : 'e.g., stanford_university'}
                  value={formData.schoolName}
                  onChange={(e) => {
                    const sanitized = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
                    setFormData({ ...formData, schoolName: sanitized });
                  }}
                  required
                />
                {formData.schoolName && !SCHOOL_NAME_REGEX.test(formData.schoolName) && (
                  <p className="text-sm text-destructive">
                    Only lowercase letters, numbers, and underscores allowed
                  </p>
                )}
                <p className="text-sm text-muted-foreground">
                  {isCollege ? 'URL-slug identifier (lowercase, underscores only)' : 'Unique identifier (lowercase, underscores only)'}
                </p>
              </div>

              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="schoolTitle">
                  {label} Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="schoolTitle"
                  placeholder={isCollege ? 'e.g., Massachusetts Institute of Technology' : 'e.g., Stanford University'}
                  value={formData.schoolTitle}
                  onChange={(e) =>
                    setFormData({ ...formData, schoolTitle: e.target.value })
                  }
                  required
                />
              </div>

              {/* Nickname */}
              <div className="space-y-2">
                <Label htmlFor="nickname">
                  Nickname {isCollege && <span className="text-destructive">*</span>}
                </Label>
                <Input
                  id="nickname"
                  value={formData.nickname}
                  onChange={(e) =>
                    setFormData({ ...formData, nickname: e.target.value })
                  }
                  placeholder={isCollege ? 'e.g., MIT' : 'Optional school nickname'}
                  required={isCollege}
                />
              </div>

              {/* Website */}
              <div className="space-y-2">
                <Label htmlFor="website">
                  Website {isCollege && <span className="text-destructive">*</span>}
                </Label>
                <Input
                  id="website"
                  type="url"
                  value={formData.website}
                  onChange={(e) =>
                    setFormData({ ...formData, website: e.target.value })
                  }
                  placeholder={isCollege ? 'https://example.edu' : 'https://example.com'}
                  required={isCollege}
                />
              </div>

              {/* NCES Code / IPEDS ID */}
              <div className="space-y-2">
                <Label htmlFor="ncesCode">
                  {isCollege
                    ? (formData.country === 'CA' ? 'Institution ID' : 'IPEDS ID')
                    : 'NCES Code'}
                  {!isCollege && formData.country === 'CA' && (
                    <span className="ml-2 text-xs text-muted-foreground">(Optional for Canadian schools)</span>
                  )}
                </Label>
                <Input
                  id="ncesCode"
                  value={formData.ncesCode}
                  onChange={(e) =>
                    setFormData({ ...formData, ncesCode: e.target.value })
                  }
                  placeholder={isCollege
                    ? (formData.country === 'CA' ? 'Enter Institution ID' : 'Enter IPEDS ID')
                    : 'Enter NCES code'}
                />
              </div>

              {/* Enrollment */}
              <div className="space-y-2">
                <Label htmlFor="enrollment">
                  Enrollment {isCollege && <span className="text-destructive">*</span>}
                </Label>
                <Input
                  id="enrollment"
                  type="number"
                  min="0"
                  value={formData.studentsCount}
                  onChange={(e) =>
                    setFormData({ ...formData, studentsCount: parseInt(e.target.value) || 0 })
                  }
                  required={isCollege}
                />
              </div>

              {/* Status */}
              <div className="space-y-2">
                <Label htmlFor="status">
                  Status <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.status}
                  onValueChange={(value: SchoolStatus) =>
                    setFormData({ ...formData, status: value })
                  }
                >
                  <SelectTrigger id="status" className="w-full">
                    <SelectValue>
                      <Badge variant={getStatusBadgeVariant(formData.status || 'waitlist')}>
                        {SCHOOL_STATUSES.find((s) => s.value === formData.status)?.label || formData.status}
                      </Badge>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {SCHOOL_STATUSES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        <Badge variant={getStatusBadgeVariant(s.value)}>
                          {s.label}
                        </Badge>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Lite Level - school only */}
              {!isCollege && formData.status !== 'live' && (
                <div className="space-y-2">
                  <Label htmlFor="liteLevel">Lite Level</Label>
                  <Select
                    value={formData.liteLevel}
                    onValueChange={(value: any) =>
                      setFormData({ ...formData, liteLevel: value })
                    }
                  >
                    <SelectTrigger id="liteLevel">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LITE_LEVELS.map((ll) => (
                        <SelectItem key={ll.value} value={ll.value}>
                          {ll.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
                    </div>

                    {/* Right Column - Domains */}
                    <div className="space-y-5">
                      <h3 className="font-semibold text-sm flex items-center gap-2 text-muted-foreground uppercase tracking-wide">
                        <Mail className="h-4 w-4" />
                        Email Domains
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Add the email domains for this {label.toLowerCase()}
                      </p>
                      <div className="flex gap-2">
                        <Input
                          placeholder={isCollege ? 'e.g., mit.edu' : 'e.g., stanford.edu'}
                          value={domainInput}
                          onChange={(e) => setDomainInput(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addDomain();
                            }
                          }}
                        />
                        <Button type="button" onClick={addDomain}>
                          Add
                        </Button>
                      </div>
                      {formData.domains && formData.domains.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {formData.domains.map((domain) => (
                            <Badge
                              key={domain}
                              variant="secondary"
                              className="gap-1"
                            >
                              {domain}
                              <button
                                type="button"
                                onClick={() => removeDomain(domain)}
                                className="ml-1 hover:text-destructive"
                              >
                                ×
                              </button>
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
          </Card>

          {/* Location Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Location Information
              </CardTitle>
              <CardDescription>
                Geographic location and timezone
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Address Geocoder */}
              <div className="space-y-2 p-4 rounded-lg border-2 border-dashed bg-muted/50">
                <Label htmlFor="addressInput" className="text-base font-semibold">
                  Quick Address Lookup
                </Label>
                <p className="text-sm text-muted-foreground">
                  Start typing an address to see suggestions and select one to auto-populate location fields
                </p>
                <div className="relative" ref={addressSuggestionsRef}>
                  <div className="relative">
                    <Input
                      id="addressInput"
                      placeholder={isCollege
                        ? 'e.g., 77 Massachusetts Ave, Cambridge, MA 02139'
                        : 'e.g., 450 Serra Mall, Stanford, CA 94305'}
                      value={addressInput}
                      onChange={(e) => setAddressInput(e.target.value)}
                      onFocus={() => {
                        if (addressSuggestions.length > 0) {
                          setShowSuggestions(true);
                        }
                      }}
                      autoComplete="off"
                    />
                    {isLoadingSuggestions && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  
                  {/* Suggestions Dropdown */}
                  {showSuggestions && addressSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-60 overflow-auto">
                      {addressSuggestions.map((suggestion, index) => (
                        <button
                          key={suggestion.place_id || index}
                          type="button"
                          onClick={() => handleSelectSuggestion(suggestion)}
                          className="w-full text-left px-4 py-3 hover:bg-accent hover:text-accent-foreground transition-colors border-b last:border-b-0 focus:bg-accent focus:outline-none"
                        >
                          <div className="flex items-start gap-2">
                            <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium">
                                {cleanAddressDisplay(suggestion.display_name, suggestion.address)}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Country */}
              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Select
                  value={formData.country}
                  onValueChange={(value: any) => {
                    setFormData({ ...formData, country: value, state: '' });
                  }}
                >
                  <SelectTrigger id="country">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* State/Province */}
              <div className="space-y-2">
                <Label htmlFor="state">
                  {formData.country === 'CA' ? 'Province/Territory' : 'State'}
                  {isCollege && <> <span className="text-destructive">*</span></>}
                </Label>
                <Select
                  value={formData.state}
                  onValueChange={(value) =>
                    setFormData({ ...formData, state: value })
                  }
                >
                  <SelectTrigger id="state">
                    <SelectValue
                      placeholder={`Select ${formData.country === 'CA' ? 'province' : 'state'}`}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {stateOptions.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Latitude */}
              <div className="space-y-2">
                <Label htmlFor="lat">
                  Latitude {isCollege && <span className="text-destructive">*</span>}
                </Label>
                <Input
                  id="lat"
                  type="number"
                  step="any"
                  value={formData.lat ?? ''}
                  onChange={(e) =>
                    setFormData({ ...formData, lat: e.target.value ? parseFloat(e.target.value) : undefined })
                  }
                  placeholder="e.g., 42.3601"
                />
              </div>

              {/* Longitude */}
              <div className="space-y-2">
                <Label htmlFor="lng">
                  Longitude {isCollege && <span className="text-destructive">*</span>}
                </Label>
                <Input
                  id="lng"
                  type="number"
                  step="any"
                  value={formData.lng ?? ''}
                  onChange={(e) =>
                    setFormData({ ...formData, lng: e.target.value ? parseFloat(e.target.value) : undefined })
                  }
                  placeholder="e.g., -71.0589"
                />
              </div>

              {/* Timezone */}
              <div className="space-y-2">
                <Label htmlFor="timezone">
                  Timezone <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.timezone}
                  onValueChange={(value) =>
                    setFormData({ ...formData, timezone: value })
                  }
                >
                  <SelectTrigger id="timezone">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map((tz) => (
                      <SelectItem key={tz} value={tz}>
                        {tz}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* General Settings & Appearance */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                General Settings & Appearance
              </CardTitle>
              <CardDescription>
                Privacy settings and visual customization
              </CardDescription>
            </CardHeader>
            <CardContent>
                  <div className="grid gap-6 md:grid-cols-2">
                    {/* Left Column - General Settings */}
                    <div className="space-y-5">
                      <h3 className="font-semibold text-sm flex items-center gap-2 text-muted-foreground uppercase tracking-wide">
                        <Settings className="h-4 w-4" />
                        General Settings
                      </h3>
                      <div className="flex items-center justify-between gap-4 p-4 rounded-lg border">
                        <div className="flex-1">
                          <p className="font-medium">Private {label}</p>
                          <p className="text-sm text-muted-foreground mt-0.5">
                            Mark this {label.toLowerCase()} as private
                          </p>
                        </div>
                        <Switch
                          id="private"
                          checked={formData.private}
                          onCheckedChange={(checked) =>
                            setFormData({ ...formData, private: checked })
                          }
                        />
                      </div>

                      {/* Mascot Image - college only */}
                      {isCollege && (
                        <div className="space-y-2">
                          <Label>Mascot Image</Label>
                          <div className="flex items-center gap-4">
                            {mascotPreview && (
                              <div className="h-16 w-16 rounded-md border overflow-hidden shrink-0">
                                <img
                                  src={mascotPreview}
                                  alt="Mascot"
                                  className="h-full w-full object-contain"
                                />
                              </div>
                            )}
                            <input
                              ref={mascotInputRef}
                              type="file"
                              accept="image/png,image/jpeg,image/gif"
                              onChange={handleMascotChange}
                              className="hidden"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => mascotInputRef.current?.click()}
                            >
                              <Upload className="mr-2 h-4 w-4" />
                              {mascotPreview ? 'Change Mascot' : 'Upload Mascot'}
                            </Button>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Optional mascot image for the college
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Right Column - Appearance */}
                    <div className="space-y-5">
                      <h3 className="font-semibold text-sm flex items-center gap-2 text-muted-foreground uppercase tracking-wide">
                        <Palette className="h-4 w-4" />
                        Appearance
                      </h3>
                      
                      {/* Logo Upload - school only */}
                      {!isCollege && (
                        <div className="space-y-2">
                          <Label htmlFor="logo">{label} Logo</Label>
                          <div className="flex items-center gap-4">
                            {logoPreview && (
                              <div className="h-16 w-16 rounded-md border overflow-hidden shrink-0">
                                <img
                                  src={logoPreview}
                                  alt="School logo"
                                  className="h-full w-full object-contain"
                                />
                              </div>
                            )}
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept="image/png,image/jpeg,image/gif"
                              onChange={handleLogoChange}
                              className="hidden"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => fileInputRef.current?.click()}
                            >
                              <Upload className="mr-2 h-4 w-4" />
                              {logoPreview ? 'Change Logo' : 'Upload Logo'}
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      <div>
                        <Label className="text-sm font-medium mb-3 block">
                          Primary Color {isCollege && <span className="text-destructive">*</span>}
                        </Label>
                        {formData.primaryColor && (
                          <div className="mb-3 p-3 rounded-lg border bg-muted/30 flex items-center gap-3">
                            <div
                              className="h-10 w-10 rounded-md shrink-0 ring-2 ring-border/50"
                              style={{ backgroundColor: formData.primaryColor }}
                            />
                            <div className="flex-1">
                              <p className="text-sm font-medium">
                                {COLOR_SWATCHES.find(c => c.value === formData.primaryColor)?.label || 'Custom Color'}
                              </p>
                            </div>
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-2">
                          {COLOR_SWATCHES.map((color) => (
                            <button
                              key={color.value}
                              type="button"
                              onClick={() => setFormData({ ...formData, primaryColor: color.value })}
                              className={`flex items-center gap-2 p-2 rounded-lg border-2 transition-all hover:scale-105 ${
                                formData.primaryColor === color.value
                                  ? 'border-foreground ring-2 ring-ring/50'
                                  : 'border-border hover:border-foreground/50'
                              }`}
                            >
                              <div
                                className="h-6 w-6 rounded-md shrink-0 ring-1 ring-border/50"
                                style={{ backgroundColor: color.value }}
                              />
                              <span className="text-xs font-medium text-left">{color.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" asChild>
              <Link to="/dashboard/schools">Cancel</Link>
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Creating...' : `Create ${label}`}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
